/**
 * Zugriff auf Kommentare und Änderungsnachverfolgung.
 *
 * Beides braucht mindestens WordApi 1.4, das Auslesen einzelner nachverfolgter
 * Änderungen sogar 1.6. Fehlt die Unterstützung, liefern die Funktionen `null`
 * und die betroffene Aufgabe meldet offen, dass sie nicht geprüft werden kann.
 */

import { supportsWordApi, wordApi } from "./capabilities";
import { isWordAvailable, WordUnavailableError } from "./document";

export interface CommentInfo {
  authorName: string;
  content: string;
  resolved: boolean;
  replyCount: number;
}

/** Liest alle Kommentare des Haupttextes – oder `null`, wenn die API fehlt. */
export async function readComments(): Promise<CommentInfo[] | null> {
  if (!isWordAvailable()) throw new WordUnavailableError();
  if (!supportsWordApi(wordApi.comments)) return null;

  try {
    return await Word.run(async (context) => {
      const comments = context.document.body.getComments();
      comments.load("items/authorName,items/content,items/resolved,items/replies");
      await context.sync();

      return comments.items.map((comment) => ({
        authorName: typeof comment.authorName === "string" ? comment.authorName : "",
        content: typeof comment.content === "string" ? comment.content.trim() : "",
        resolved: comment.resolved === true,
        replyCount: Array.isArray(comment.replies) ? comment.replies.length : 0,
      }));
    });
  } catch {
    return null;
  }
}

/**
 * Zustand der Änderungsnachverfolgung: "Off", "TrackAll" oder "TrackMineOnly".
 * `null`, wenn die API fehlt.
 */
export async function readChangeTrackingMode(): Promise<string | null> {
  if (!isWordAvailable()) throw new WordUnavailableError();
  if (!supportsWordApi(wordApi.changeTracking)) return null;

  try {
    return await Word.run(async (context) => {
      context.document.load("changeTrackingMode");
      await context.sync();
      return typeof context.document.changeTrackingMode === "string"
        ? context.document.changeTrackingMode
        : null;
    });
  } catch {
    return null;
  }
}

export function isTrackingOn(mode: string | null): boolean {
  return mode === "TrackAll" || mode === "TrackMineOnly";
}

/**
 * Anzahl der noch offenen nachverfolgten Änderungen – oder `null`, wenn die
 * dafür nötige WordApi 1.6 nicht verfügbar ist.
 */
export async function countTrackedChanges(): Promise<number | null> {
  if (!isWordAvailable()) throw new WordUnavailableError();
  if (!supportsWordApi(wordApi.trackedChanges)) return null;

  try {
    return await Word.run(async (context) => {
      const changes = context.document.body.getTrackedChanges();
      changes.load("items/type");
      await context.sync();
      return changes.items.length;
    });
  } catch {
    return null;
  }
}
